import { useLocation, useNavigate } from '@remix-run/react';
import type { TPostActionsElement } from '@shared/dream-components';
import { PostActionsElement } from '@shared/dream-components';
import cx from 'classnames';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import PostLikeAction from '~/components/PostLikeAction';
import { usePostPageContext } from '~/context/usePostPageContext';

import usePostInsights from '~/hooks/useLoaders/usePostInsights';
import useScrollDetection from '~/hooks/useScrollDetection';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import type { PostLoaderData } from '~/routes/p/$slug';

interface Props {
  node: TPostActionsElement;
  children: ReactNode | ReactNode[];
}

const PostActions = ({ node, children }: Props) => {
  const { t } = useTranslation();
  const { post } = useTypedLoaderData<PostLoaderData>();
  const postInsights = usePostInsights(post.id);
  const { canShowCommentsNode } = usePostPageContext() || { canShowCommentsNode: false };

  const navigate = useNavigate();
  const location = useLocation();

  const { canScrollVertically, hasVerticalScrolling } = useScrollDetection({
    delay: 500,
  });

  const handleClickComment = () => {
    try {
      if (typeof document !== 'undefined') {
        const commentElement = document.querySelector('#comments');
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
    } catch {
      // do nothing
    }

    // Fallback
    navigate(`${location.pathname}#comments`);
  };

  const handleClickBack = () => {
    navigate('/');
  };

  const shareUrl = typeof document !== 'undefined' ? document.location.href : '#';

  if (node?.attrs?.isEnabled === false) {
    return null;
  }

  return (
    <div
      className={cx(
        'fixed bottom-0 z-30 w-full transition-transform',
        hasVerticalScrolling && !canScrollVertically ? 'translate-y-full' : 'translate-y-0',
      )}
    >
      <PostLikeAction areLikesFetched={postInsights.fetched} likes={postInsights.likes} postId={post.id}>
        {({ isLiked, likesCount }) => (
          <PostActionsElement
            element={{
              ...node,
              attrs: {
                ...node.attrs,
                data: {
                  commentsCount: postInsights.comment_count,
                  likesCount: likesCount,
                  isLiked,
                  share: {
                    url: shareUrl,
                    title: post.web_title,
                    description: post.web_subtitle || '',
                  },
                },
              },
            }}
            onCommentClick={handleClickComment}
            onBackClick={handleClickBack}
            showCommentsButton={canShowCommentsNode}
            isLive
            translations={{
              'search.pagination.back': t('search.pagination.back'),
              'search.pagination.like': t('search.pagination.like'),
              'search.pagination.comment': t('search.pagination.comment'),
              'search.pagination.share': t('search.pagination.share'),
            }}
          >
            {children}
          </PostActionsElement>
        )}
      </PostLikeAction>
    </div>
  );
};

export { PostActions };
